// src/features/orders/components/OrderWorkspace.tsx
import { useEffect } from 'react';
import type { Order } from '../types/orders.types';
import { WorkspaceHeader } from './WorkspaceHeader';
import { WorkspaceSection } from './WorkspaceSection';

interface OrderWorkspaceProps {
  order: Order | null;
  onClose: () => void;
}

interface SummaryRowProps {
  label: string;
  value: string;
}

// Neutral placeholder copy for every section that has no real data source
// yet. Never "no data" — this is scaffolding for the future Orders domain,
// not a missing-value state.
const PLACEHOLDER_TEXT = 'پس از یکپارچه‌سازی با Core Orders در دسترس خواهد بود.';

function SummaryRow({ label, value }: SummaryRowProps) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-xs text-[var(--vania-colors-text-secondary,#a3aab8)]">{label}</span>
      <span className="text-sm font-medium text-[var(--vania-colors-text-primary,#f5f6fa)]">
        {value}
      </span>
    </div>
  );
}

function WorkspacePlaceholder() {
  return <p className="text-xs text-[var(--vania-colors-text-secondary,#a3aab8)]">{PLACEHOLDER_TEXT}</p>;
}

/**
 * Foundation for the future Orders domain workspace — not a details
 * redesign. Summary is the only section backed by real `Order` fields;
 * Timeline / Service / Payment / Customer / Notes / Internal Events /
 * Actions exist structurally now so future domain data has a home, but
 * render a neutral placeholder until that data exists.
 */
export function OrderWorkspace({ order, onClose }: OrderWorkspaceProps) {
  useEffect(() => {
    if (!order) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [order, onClose]);

  if (!order) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex">
      <button
        type="button"
        aria-label="بستن فضای کاری سفارش"
        onClick={onClose}
        className="flex-1 bg-black/50"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="فضای کاری سفارش"
        className="flex h-full w-full max-w-sm flex-col border-l border-[var(--vania-colors-border-subtle,rgba(255,255,255,0.08))] bg-[var(--vania-surface-raised,#15161c)]"
      >
        <WorkspaceHeader order={order} onClose={onClose} />

        <div className="flex-1 overflow-y-auto px-4">
          <WorkspaceSection title="خلاصه" defaultOpen>
            <SummaryRow label="شناسه سفارش" value={order.id} />
            <SummaryRow label="مشتری" value={order.customerName} />
            <SummaryRow label="خدمت" value={order.serviceName} />
            <SummaryRow label="مبلغ" value={order.amount} />
            <SummaryRow label="تاریخ" value={order.date} />
          </WorkspaceSection>

          <WorkspaceSection title="جدول زمانی">
            <WorkspacePlaceholder />
          </WorkspaceSection>

          <WorkspaceSection title="خدمت">
            <WorkspacePlaceholder />
          </WorkspaceSection>

          <WorkspaceSection title="پرداخت">
            <WorkspacePlaceholder />
          </WorkspaceSection>

          <WorkspaceSection title="مشتری">
            <WorkspacePlaceholder />
          </WorkspaceSection>

          <WorkspaceSection title="یادداشت‌ها">
            <WorkspacePlaceholder />
          </WorkspaceSection>

          <WorkspaceSection title="رویدادهای داخلی">
            <WorkspacePlaceholder />
          </WorkspaceSection>

          <WorkspaceSection title="عملیات">
            <WorkspacePlaceholder />
          </WorkspaceSection>
        </div>
      </div>
    </div>
  );
}
